import htmlToReact, { type DOMNode, domToReact } from 'html-react-parser';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Linkify from 'react-linkify';
import { Link } from 'react-router-dom';

import { CommunityFeedItemDto } from 'src/Services/API/generated';
import { renderTextWithMentions } from 'src/Utils/renderMentions';
import { renderWhatsappFormatting } from 'src/Utils/renderWhatsappFormatting';

import styles from './CommunityFeedItem.module.scss';
import { communityFeedItemUrl } from './communityFeedItemUrl';

const MENTION_REGEX = /@\[([^\]]+)\]\(([^)]+)\)/;

/**
 * From a CommunityFeedItemDto, return its formatted message as a simple React DOM or string.
 *
 * - Transform the string to its React DOM equivalent.
 * - Generate the proper URL if a link is present in the message.
 * - Change the <a> html markup to <Link> react elements.
 * - Render @mentions with styled spans.
 * - Render WhatsApp-style formatting (*bold*, _italic_, ~strike~, ```code```).
 */
export function useCommunityFeedItemFormatContent(feedItem: CommunityFeedItemDto) {
  const { i18n } = useTranslation();

  const langKey = i18n.language.split('-')[0] as 'en' | 'fr';
  let text = feedItem.content[langKey] ?? feedItem.content.en;
  const url = communityFeedItemUrl(feedItem) ?? '';
  text = text.replace('{{URL}}', url);

  // Is user generated content
  const isUGC = feedItem.type.startsWith('POST');

  const linkifyComponentDecorator = (href: string, text: string, key: number) => (
    <a href={href} key={key} target="_blank" rel="noopener noreferrer">
      {text}
    </a>
  );

  let jsx: React.ReactNode;

  if (isUGC) {
    const renderPlain = (plain: string) => {
      if (!MENTION_REGEX.test(plain)) {
        return <Linkify componentDecorator={linkifyComponentDecorator}>{plain}</Linkify>;
      }

      // Render mentions as styled spans, then linkify text-only parts
      const mentionNodes = renderTextWithMentions(plain, styles.mention);
      return (
        <>
          {mentionNodes.map((node, i) =>
            typeof node === 'string' ? (
              <Linkify key={i} componentDecorator={linkifyComponentDecorator}>
                {node}
              </Linkify>
            ) : (
              <React.Fragment key={i}>{node}</React.Fragment>
            )
          )}
        </>
      );
    };

    // *gras*, _italique_... facon WhatsApp, sans toucher aux liens ni aux mentions.
    jsx = renderWhatsappFormatting(text, renderPlain);
  } else {
    jsx = htmlToReact(text, {
      replace(_domNode) {
        // @ts-expect-error attribs is not defined in the typescript
        if ((_domNode as unknown as Element).attribs) {
          const domNode = _domNode as unknown as Element & {
            attribs: Record<string, string>;
            name: string;
            children: DOMNode[];
          };

          if (domNode.name === 'a' && domNode.attribs.href) {
            // Replace hyperlinks with <Link> if the href is valid...
            return <Link to={domNode.attribs.href}>{domToReact(domNode.children)}</Link>;
          } else if (domNode.name === 'a') {
            // otherwise delete the link.
            return <span>{domToReact(domNode.children)}</span>;
          }
        }
      },
    });
  }

  return { jsx, text };
}
